import {
    AlertCircle,
    CheckCircle,
    Clock,
    Pause,
    Play,
    XCircle
} from 'lucide-react';
import type { Execution } from '../../types/api';

interface ExecutionStatusBadgeProps {
  status: Execution['status'];
}

/**
 * Displays an execution status badge with appropriate icon, color, and tooltip
 */
export function ExecutionStatusBadge({ status }: ExecutionStatusBadgeProps) {
  /**
   * Get status icon for execution
   */
  const getStatusIcon = (status: Execution['status']) => {
    const iconClasses = 'w-4 h-4';
    switch (status) {
      case 'completed':
        return <CheckCircle className={`text-green-600 dark:text-green-400 ${iconClasses}`} />;
      case 'failed':
        return <XCircle className={`text-red-600 dark:text-red-400 ${iconClasses}`} />;
      case 'running':
        return <Play className={`text-blue-600 dark:text-blue-400 ${iconClasses}`} />;
      case 'pending':
        return <Clock className={`text-yellow-600 dark:text-yellow-400 ${iconClasses}`} />;
      case 'cancelled':
        return <Pause className={`text-gray-600 dark:text-gray-400 ${iconClasses}`} />;
      default:
        return <AlertCircle className={`text-gray-600 dark:text-gray-400 ${iconClasses}`} />;
    }
  };

  /**
   * Get status badge styling
   */
  const getStatusBadge = (status: Execution['status']) => {
    const baseClasses = 'inline-flex items-center px-2 py-1 text-xs font-medium rounded-full gap-1';
    switch (status) {
      case 'completed':
        return `${baseClasses} bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300`;
      case 'failed':
        return `${baseClasses} bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300`;
      case 'running':
        return `${baseClasses} bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300`;
      case 'pending':
        return `${baseClasses} bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300`;
      case 'cancelled':
        return `${baseClasses} bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300`;
      default:
        return `${baseClasses} bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300`;
    }
  };

  /**
   * Get status description for tooltips
   */
  const getStatusDescription = (status: Execution['status']) => {
    switch (status) {
      case 'completed':
        return 'Execution finished successfully';
      case 'failed':
        return 'Execution encountered an error and failed';
      case 'running':
        return 'Execution is currently in progress';
      case 'pending':
        return 'Execution is waiting to start';
      case 'cancelled':
        return 'Execution was cancelled by user';
      default:
        return 'Unknown execution status';
    }
  };

  return (
    <span 
      className={getStatusBadge(status)}
      title={getStatusDescription(status)}
    >
      {getStatusIcon(status)}
      {status}
    </span>
  );
} 